export interface TvCastWorkItem {
  studentName: string;
  original: string;
  result: string;
}

const ORIGINAL_KEYS = [
  'promptImg',
  'promptImgUrl',
  'original',
  'originalUrl',
  'lineImg',
  'lineImgUrl',
  'sketchImg',
  'sketchImgUrl',
];

const RESULT_KEYS = [
  'newImg',
  'newImgUrl',
  'imgUrl',
  'result',
  'resultUrl',
  'colourImgUrl',
  'colorImgUrl',
  'colorImg',
  'colourImg',
  'processedImg',
  'processedImgUrl',
  'aiImgUrl',
  'imageUrl',
  'url',
];

function isHttpUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//.test(value);
}

function pickImageUrl(raw: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = raw[key];
    if (isHttpUrl(value)) {
      return value;
    }
  }
  return '';
}

export function unwrapCastPayloads(raw: unknown): Array<Record<string, unknown>> {
  if (raw == null || raw === '') {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw.flatMap((item) => unwrapCastPayloads(item));
  }

  if (typeof raw === 'string') {
    try {
      return unwrapCastPayloads(JSON.parse(raw));
    } catch {
      return isHttpUrl(raw) ? [{ imgUrl: raw }] : [];
    }
  }

  if (typeof raw !== 'object') {
    return [];
  }

  const obj = raw as Record<string, unknown>;
  const nested = obj.data ?? obj.content ?? obj.work ?? obj.works ?? obj.list ?? obj.items;
  if (nested != null && nested !== obj) {
    const nestedItems = unwrapCastPayloads(nested);
    if (nestedItems.length) {
      return nestedItems;
    }
  }

  return [obj];
}

export function getCastWorkLogId(raw: Record<string, unknown>): string {
  const id = raw.logId ?? raw.logID ?? raw.imgLogId ?? raw.aiClassRoomWorkId;
  if (id != null && id !== '') {
    return String(id);
  }
  const original = pickImageUrl(raw, ORIGINAL_KEYS);
  const result = pickImageUrl(raw, RESULT_KEYS);
  const name = String(raw.studentName ?? raw.student_name ?? '');
  if (original || result) {
    return `${name}|${original}|${result}`;
  }
  return '';
}

export function normalizeTvCastWork(raw: Record<string, unknown>): TvCastWorkItem {
  const original = pickImageUrl(raw, ORIGINAL_KEYS);
  let result = pickImageUrl(raw, RESULT_KEYS);

  // 部分消息只带一个 url 字段，且与原图不同时视为效果图
  if (!result && isHttpUrl(raw.url) && raw.url !== original) {
    result = raw.url;
  }

  return {
    studentName: String(raw.studentName ?? raw.student_name ?? raw.name ?? '').trim(),
    original,
    result,
  };
}

export function normalizeTvCastWorks(list: Array<Record<string, unknown>>): TvCastWorkItem[] {
  return list
      .flatMap((item) => unwrapCastPayloads(item))
      .map((item) => normalizeTvCastWork(item))
      .filter((item) => item.original || item.result);
}
